import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import ReactRouterPropTypes from 'react-router-prop-types'
import clsx from 'clsx'
import SettingsRoundedIcon from '@material-ui/icons/SettingsRounded'
import { makeStyles } from '@material-ui/core/styles'
import {
  IconButton,
  AppBar,
  Toolbar,
  Popover,
  List,
  Grid,
  Hidden,
  Avatar,
  Modal,
  Backdrop,
  Fade,
  ListSubheader,
  TextField,
} from '@material-ui/core'
import { Button, Typography } from '@eqworks/react-labs'
import { getSelectorName } from './utils'
import { menuConfig } from './config'
import NavContent from './nav-content'
import { checkUserRoles, getVisibleRoutes } from '../../../../../helpers/utils/auth'
import { routes } from '../../../route-config'
import { withBaseRoute } from '../../../../../helpers/utils/routing'
import { Autocomplete } from '@material-ui/lab'
import { useWhiteLabels, useCustomers, useWlCuName } from '../../../../../hooks'
import EasterLogo from '../../../../../assets/logo/easter_logo.png'
import HalloweenLogo from '../../../../../assets/logo/halloween_logo.png'
import WinterLogo from '../../../../../assets/logo/winter_logo.png'
import { isFestivalSeason } from '../../../../../helpers/utils/time'

import Snowfall from 'react-snowfall'

const useStyles = makeStyles((theme) => ({
  appbar: {
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
  avatar: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(2),
  },
  toolbar: {
    backgroundColor: theme.palette.common.white,
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 3),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 4),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 8),
    },
  },
  buttonGroup: {
    '&>*': {
      margin: theme.spacing(1),
    },
  },
  logo: {
    display: 'flex',
  },
  modalContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 4,
    boxShadow: theme.shadows[4],
    '&:focus': {
      outline: 'none',
    },
  },
  list: {
    overflow: 'auto',
    //height: '70vh',
    width: '70vh',
  },
  listContent: {
    padding: theme.spacing(2, 2),
  },
  subheader: {
    boxShadow: theme.shadows[2],
    '&.MuiListSubheader-root': {
      ...theme.typography.subtitle1,
      color: theme.palette.secondary[700],
      padding: theme.spacing(2),
    },
  },
  navItem: {
    color: theme.palette.common.black,
  },
}))

const isEasterSeason = isFestivalSeason('Easter')
const isHalloweenSeason = isFestivalSeason('Halloween')
const isWinterHolidays = isFestivalSeason('Winter Holidays')

//This is a temporary fix for handling SUI modals.

const observedBody = document.body

const modalTargetClass = 'ui page modals dimmer transition visible active'

const observer = new MutationObserver((mutationRecords) => {

  mutationRecords.forEach(mutation => {
    const navbar = document.getElementById('locus-navbar')
    if (mutation.addedNodes.length && mutation.addedNodes[0].className === modalTargetClass) {
      navbar.style.display = 'none'
    }
    else if (mutation.removedNodes.length && mutation.removedNodes[0].className === modalTargetClass) {
      navbar.style.display = ''
    }
  })
})

observer.observe(observedBody, {
  attributes: true,
  characterData: true,
  childList: true,
  addedNodes: true,
  removedNodes: true,
})

const MuiNav = ({
  location,
  logOut,
  logOutAll,
  email,
  access,
  baseRoute,
}) => {
  const classes = useStyles()

  const history = useHistory()

  const { basePath, params: { wl = 0, cu = 0 } } = baseRoute

  // change path only after cu selected (for wl/cu change on nav selector only, not home page selector)
  const [_whitelabel, _setWhitelabel] = useState(wl)
  useEffect(() => {
    const page = (location.pathname.split('/')).slice(-1)[0]
    if (page === 'home') {
      _setWhitelabel(wl)
    }
  }, [wl, location.pathname])

  const [, wlList] = useWhiteLabels()
  const [, cuList] = useCustomers(_whitelabel)
  const [, cuName] = useWlCuName(_whitelabel, cu)
  const _wl = wlList.find(({ whitelabelid }) => whitelabelid === Number(_whitelabel))
  const { company: wlName = '' } = _wl || {}

  // Don't keep open wl selector on Finance page when no wl selected
  const userRoles = checkUserRoles(access)
  const visibleRoutes = getVisibleRoutes({ userRoles, ...access })
  const routesRendered = routes
    .filter(({ section, hideNav }) => visibleRoutes.includes(section) && !hideNav)
  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [menuItems, setMenuItems] = useState('')
  const [contentType, setContentType] = useState('')
  const [capturedY, setCapturedY] = useState(0)

  const logoSrc = useMemo(() => {
    if (isEasterSeason()) {
      return EasterLogo
    }
    if (isHalloweenSeason()) {
      return HalloweenLogo
    }
    if (isWinterHolidays()) {
      return WinterLogo
    }
    return 'https://locus-cdn.s3.amazonaws.com/logos/Locus_logo.svg'
  }, [])

  useEffect(() => {
    const handleScroll = () => capturedY !== window.pageYOffset && setOpen(false)

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [capturedY])

  const handleModal = (isOpen) => {
    // if closed modal without selecing cu, revert back to previously selected wl/cu
    if (!cuName) {
      _setWhitelabel(wl)
    }
    const style = isOpen ? 'hidden' : 'auto'
    setModalOpen(isOpen)
    document.body.style.overflow = style
  }

  const updateWLCU = (update, oldBasePath) => {
    const { pathname, search, hash } = location
    let basePath = ''
    if (Object.keys(update).length > 0) {
      const { wl, cu } = update
      if (wl) {
        basePath = `/${wl}`
        if (cu) {
          basePath += `/${cu}`
        }
      }
    }
    const finalPath = `${pathname.replace(
      oldBasePath,
      basePath,
    )}${search}${hash}`
    history.push(finalPath)
  }

  const handleClick = (event, root = '') => {
    setAnchorEl(event.currentTarget)
    setMenuItems(root)
    setOpen(true)
    setContentType(event.currentTarget.id)
    setCapturedY(window.scrollY)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setOpen(false)
  }

  const handleWlChange = (_, value) => {
    _setWhitelabel(value.whitelabelid)
  }

  const handleCuChange = (_, value) => {
    const cu = value.agencyid
    updateWLCU({ wl: _whitelabel, cu }, basePath)
    setModalOpen(false)
  }

  const getParentMenu = (menu) => menuConfig.find(({ children }) => children.includes(menu))?.root

  const finalRoutes = routesRendered.map(route => ({ ...route, root: getParentMenu(route.content) }))

  return (
    <>
      <Modal
        className={classes.modalContainer}
        open={modalOpen}
        onClose={() => handleModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={modalOpen}>
          <List
            className={clsx(classes.list, classes.paper)}
            subheader={
              <ListSubheader
                className={classes.subheader}
              >
                Select whitelabel and customer
              </ListSubheader>
            }
          >
            <div className={classes.listContent}>
              {wlList !== null && (
                <Autocomplete
                  style={{ padding: '8px 16px' }}
                  options={wlList}
                  defaultValue={_wl}
                  getOptionLabel={(item) => item.company}
                  getOptionSelected={(item) => item.company === _wl.comapany}
                  renderInput={(params) =>
                    <TextField
                      {...params}
                      label='Whitelabel'
                      value={wlName}
                      variant='outlined'
                    />
                  }
                  onChange={handleWlChange}
                  label={wlName}
                />
              )}
              {cuList !== null && (
                <Autocomplete
                  style={{ padding: '8px 16px' }}
                  options={cuList}
                  getOptionLabel={(item) => item.companyname}
                  renderInput={(params) =>
                    <TextField
                      {...params}
                      placeholder={cuName}
                      label='Customer'
                      value={cuName}
                      variant='outlined'
                    />
                  }
                  onChange={handleCuChange}
                  label={cuName}
                />
              )}
            </div>
          </List>
        </Fade>
      </Modal>
      <Popover
        disableScrollLock={true}
        open={open}
        onScroll={handleClose}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={4}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <NavContent
          email={email}
          type={contentType}
          menuItems={menuItems}
          basePath={basePath}
          finalRoutes={finalRoutes}
          logOut={logOut}
          logOutAll={logOutAll}
          handleClose={handleClose}
        />
      </Popover>
      <AppBar id='locus-navbar' className={classes.appbar} elevation={2}>
        <Toolbar className={classes.toolbar}>
          <Link to={`${basePath}/home`}>
            <Grid item className={clsx(classes.navItem, classes.logo)}>
              <Avatar src={logoSrc} variant="rounded" className={classes.avatar} />
              <Hidden smDown>
                <Typography className={classes.navItem} variant="h5">
                  LOCUS
                </Typography>
              </Hidden>
            </Grid>
          </Link>
          {(wl && cu) &&
            <Grid item className={classes.navItem}>
              <div className={classes.buttonGroup}>
                {menuConfig
                  .filter(({ children }) => {
                    const children_lowercase = children.map(child => child.toLowerCase())
                    const filtered = children_lowercase.filter(child => {
                      const routes = routesRendered.map(({ section }) => section)
                      return routes.includes(child)
                    })
                    return filtered.length > 0
                  })
                  .map(({ type, root, route }) => type === 'dropdown' ?
                    (
                      <Button
                        key={root}
                        id='navItem'
                        className={classes.navItem}
                        type="tertiary"
                        onClick={(event) => handleClick(event, root)}
                      >
                        {root}
                      </Button>
                    ) : (
                      <Button key={root}
                        id='navItem'
                        component={Link}
                        className={classes.navItem}
                        type="tertiary"
                        to={`${basePath}/${route}`}
                      >
                        {root}
                      </Button>
                    ))}
              </div>
            </Grid>
          }
          <Grid item className={classes.navItem}>
            <div className={classes.buttonGroup}>
              {/* keep selector button present on nav when modal closes before cu selected */}
              {Boolean(wl) &&
                <Button className={classes.navItem} type='tertiary' onClick={() => setModalOpen(true)}>{getSelectorName(wlName, cuName)}</Button>
              }
              <IconButton color='primary' id='settings' onClick={(event) => handleClick(event)}>
                <SettingsRoundedIcon />
              </IconButton>
            </div>
          </Grid>
        </Toolbar>
        {isWinterHolidays() &&
          <Snowfall
            color='#fefefe'
            speed={[0.5, 1.0]}
            snowflakeCount={50}
          />
        }
      </AppBar>
    </>
  )
}

MuiNav.propTypes = {
  location: ReactRouterPropTypes.location.isRequired,
  logOut: PropTypes.func.isRequired,
  logOutAll: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  access: PropTypes.object.isRequired,
  baseRoute: PropTypes.object.isRequired,
}

export default withBaseRoute(MuiNav)
